import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, AlertTriangle, Printer } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { api, downloadBlob } from "@/lib/api";
import { AdminTitle, Btn, inputCls } from "@/pages/admin/Admin";
import { StatusBadge } from "@/pages/OrderPage";

const rp = (n) => "Rp " + Math.round(n).toLocaleString("id-ID");
const COLORS = ["#3B220C", "#8C4F27", "#C47A37", "#E6DFD5"];
const Stat = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-[#E6DFD5] p-6" data-testid={testid}>
    <p className="text-xs font-mono tracking-[0.15em] text-[#8C4F27]">{label}</p>
    <p className="text-3xl font-serif mt-2">{value}</p>
    {sub && <p className="text-xs text-[#6E6259] mt-1">{sub}</p>}
  </div>
);

export default function Overview() {
  const cur = new Date().toISOString().slice(0, 7);
  const [s, setS] = useState(null);
  const [month, setMonth] = useState(cur);
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: weekAgo, end: today });
  useEffect(() => {
    const load = () => api.get("/admin/stats", { params: range }).then((r) => setS(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [range]);
  if (!s) return null;
  const days = s.series.map((d) => ({ ...d, label: d.date.slice(5) }));
  const period = `${s.range.from} → ${s.range.to}`;
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <AdminTitle action={
        <div className="flex flex-wrap gap-2 items-end" data-print-hide>
          <label className="text-xs text-[#6E6259]">From<input type="date" value={range.start} max={range.end} onChange={(e) => e.target.value && setRange({ ...range, start: e.target.value })} className={`${inputCls} !w-40`} data-testid="overview-start-date" /></label>
          <label className="text-xs text-[#6E6259]">To<input type="date" value={range.end} min={range.start} onChange={(e) => e.target.value && setRange({ ...range, end: e.target.value })} className={`${inputCls} !w-40`} data-testid="overview-end-date" /></label>
          <Btn onClick={() => window.print()} data-testid="overview-print-btn" className="flex items-center gap-2 whitespace-nowrap"><Printer size={16} />Print / PDF</Btn>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} !w-40`} data-testid="report-month-input" />
          <Btn onClick={() => downloadBlob(`/admin/reports/monthly?month=${month}`, `craftendemic-report-${month}.pdf`)} data-testid="download-report-btn" className="flex items-center gap-2 whitespace-nowrap"><FileDown size={16} />Monthly PDF</Btn>
        </div>}>Overview</AdminTitle>
      <p className="text-sm text-[#6E6259] -mt-4" data-testid="overview-period">Period: {period}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="TODAY SALES" value={rp(s.today.revenue)} sub={`${s.today.orders} paid orders`} testid="stat-today-revenue" />
        <Stat label="VISITORS TODAY" value={s.today.visitors} sub={`${s.today.views} page views`} testid="stat-today-visitors" />
        <Stat label="PERIOD SALES" value={rp(s.month.revenue)} sub={`${s.month.orders} paid orders`} testid="stat-month-revenue" />
        <Stat label="CONVERSION" value={`${s.month.conversion}%`} sub={`${s.month.visitors} unique visitors in period`} testid="stat-conversion" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E6DFD5] p-6">
          <p className="text-sm mb-4">Daily sales</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={days}><XAxis dataKey="label" fontSize={10} /><YAxis fontSize={10} tickFormatter={(v) => `${v / 1000}k`} /><Tooltip formatter={(v) => rp(v)} />
              <Area dataKey="revenue" stroke="#3B220C" fill="#8C4F27" fillOpacity={0.15} /></AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E6DFD5] p-6">
          <p className="text-sm mb-4">Revenue by type</p>
          {s.by_type.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={s.by_type} dataKey="value" nameKey="type" innerRadius={50} outerRadius={90}>{s.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % 4]} />)}</Pie><Tooltip formatter={(v) => rp(v)} /></PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[#6E6259]">No sales yet.</p>}
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E6DFD5] p-6">
          <p className="text-sm mb-4">Orders & visitors per day</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={days.slice(-31)}><XAxis dataKey="label" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Bar dataKey="visitors" fill="#E6DFD5" /><Bar dataKey="orders" fill="#3B220C" /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E6DFD5] p-6" data-testid="low-stock-panel">
          <p className="text-sm mb-4 flex items-center gap-2"><AlertTriangle size={14} className="text-[#C87D17]" />Low stock</p>
          {s.low_stock.map((p) => <p key={p.product_id} className="flex justify-between text-sm py-1"><span>{p.name}</span><span className="font-mono">{p.stock}</span></p>)}
          {!s.low_stock.length && <p className="text-sm text-[#6E6259]">All good.</p>}
        </div>
        <div className="bg-white border border-[#E6DFD5] p-6">
          <p className="text-sm mb-4">Recent orders · <Link to="/admin/orders" className="underline">{s.pending_orders} to fulfil</Link></p>
          {s.recent.map((o) => <div key={o.order_id} className="flex items-center justify-between text-xs py-1.5 gap-2"><span className="font-mono">{o.order_id}</span><StatusBadge s={o.status} /><span>{rp(o.total)}</span></div>)}
        </div>
      </div>
      <div className="bg-white border border-[#E6DFD5]" data-testid="visitors-daily-table">
        <p className="p-4 text-sm border-b border-[#E6DFD5]">Daily visitors & sales · {period}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-[#6E6259] border-b border-[#E6DFD5]"><tr>{["Date", "Visitors", "Page views", "Paid orders", "Revenue"].map((h) => <th key={h} className="p-3 font-normal">{h}</th>)}</tr></thead>
            <tbody>
              {[...s.series].reverse().map((d) => (
                <tr key={d.date} className="border-b border-[#E6DFD5] last:border-0" data-testid={`visitors-row-${d.date}`}>
                  <td className="p-3 font-mono text-xs">{d.date}</td><td className="p-3">{d.visitors}</td><td className="p-3">{d.views}</td><td className="p-3">{d.orders}</td><td className="p-3">{rp(d.revenue)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="font-medium border-t border-[#E6DFD5]"><td className="p-3">Total</td><td className="p-3">{s.month.visitors} unique</td><td className="p-3">{s.series.reduce((a, d) => a + d.views, 0)}</td><td className="p-3">{s.month.orders}</td><td className="p-3">{rp(s.month.revenue)}</td></tr></tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
